import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";

const LOGO_URL = "/logo-infirmiers.png";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("loading"); // loading, success, already, error
  const [message, setMessage] = useState("");
  const hasRun = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Lien de vérification invalide.");
      return;
    }
    // Guard against React.StrictMode double-invocation which would fire a 2nd
    // verify call returning already_verified and hiding the success screen.
    if (hasRun.current) return;
    hasRun.current = true;

    const verifyEmail = async () => {
      try {
        const response = await fetch(`${API}/auth/verify-email?token=${token}`);
        const data = await response.json();

        if (response.ok) {
          if (data.already_verified) {
            setStatus("already");
            setMessage(data.message);
          } else {
            setStatus("success");
            setMessage(data.message);
          }
        } else {
          setStatus("error");
          setMessage(data.detail || "Une erreur est survenue.");
        }
      } catch (error) {
        setStatus("error");
        setMessage("Impossible de contacter le serveur.");
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0D1C32] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="Infirmiers Privés" className="h-12 mx-auto" />
          </Link>
        </div>

        <div className="card-dark p-8 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 text-[#00E073] animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Vérification en cours...</h2>
              <p className="text-[#94A3B8]">Nous vérifions votre adresse email.</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 text-[#00E073] mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Email vérifié !</h2>
              <p className="text-[#94A3B8] mb-6">{message}</p>
              <Link to="/login">
                <Button className="btn-primary w-full" data-testid="go-to-login-btn">
                  Se connecter
                </Button>
              </Link>
            </>
          )}

          {status === "already" && (
            <>
              <Mail className="w-16 h-16 text-[#28CAD4] mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Déjà vérifié</h2>
              <p className="text-[#94A3B8] mb-6">{message}</p>
              <Link to="/login">
                <Button className="btn-primary w-full" data-testid="already-verified-login-btn">Se connecter</Button>
              </Link>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Erreur de vérification</h2>
              <p className="text-[#94A3B8] mb-6">{message}</p>
              <Link to="/login">
                <Button className="btn-secondary w-full" data-testid="verify-error-login-btn">Retour à la connexion</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
